import { useRef } from "react"

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import type { Modifier } from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"

import PriorityItem from "./PriorityItem"
import { Priority } from "./types"

interface PriorityListProps {
  priorities: Priority[]
  onReorder: (priorities: Priority[]) => void
}

const PriorityList = ({ priorities, onReorder }: PriorityListProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const restrictToContainer: Modifier = ({ transform, draggingNodeRect }) => {
    const container = containerRef.current
    if (!container || !draggingNodeRect) {
      return { ...transform, x: 0 }
    }

    const containerRect = container.getBoundingClientRect()
    const topBound = containerRect.top - draggingNodeRect.top
    const bottomBound = containerRect.bottom - draggingNodeRect.bottom

    return {
      ...transform,
      x: 0,
      y: Math.min(Math.max(transform.y, topBound), bottomBound),
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    const oldIndex = priorities.findIndex((p) => p.key === active.id)
    const newIndex = priorities.findIndex((p) => p.key === over.id)
    onReorder(arrayMove(priorities, oldIndex, newIndex))
  }

  return (
    <div className="flex h-full flex-col">
      <p className="text-sm font-semibold text-palette-jungleGreen900">Priority per role</p>
      <p className="mb-3 text-xs text-palette-jungleGreen500">Drag and drop to reorder</p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToContainer]}
      >
        <SortableContext
          items={priorities.map((p) => p.key)}
          strategy={verticalListSortingStrategy}
        >
          <div ref={containerRef} className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            {priorities.map((priority, index) => (
              <PriorityItem key={priority.key} priority={priority} index={index} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

export default PriorityList
